import { Box, Chip, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Widget } from "../types/widget";
import { formatWidgetOutputLabel } from "../utils/widgetDisplay";
import { useAppCustomStyles } from "../assets/theme/custom/hooks";

type Props = {
  draft: Widget;
  outputFormatLabel?: string;
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;

  return (
    <Box sx={styles.draftSummaryRow}>
      <Typography variant="caption" color="text.secondary" sx={styles.draftSummaryLabel}>
        {label}
      </Typography>
      <Typography variant="body2" sx={styles.draftSummaryValue}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function WidgetDraftSummary({ draft, outputFormatLabel }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;
  const format = outputFormatLabel || formatWidgetOutputLabel(draft);
  const paramCount = draft.dataSource?.parameters?.length ?? 0;
  const dslKeys = Object.keys(draft.dataSource?.dslTemplate ?? {}).length;

  return (
    <Box sx={styles.draftSummaryRoot}>
      <Stack spacing={0.25}>
        {draft.description?.trim() && (
          <SummaryRow label={t("widget.description")} value={draft.description.trim()} />
        )}
        <SummaryRow
          label={t("widget.indices")}
          value={(draft.dataSource?.indices || []).join(", ")}
        />
        {draft.dataSource?.timeField && (
          <SummaryRow label={t("widget.timeField")} value={draft.dataSource.timeField} />
        )}
        <SummaryRow label={t("widget.outputFormat")} value={format} />
        <Box sx={styles.draftSummaryChipsRow}>
          <Chip
            size="small"
            variant="outlined"
            label={t("widget.draftSummaryDsl", { count: dslKeys })}
            sx={styles.draftSummaryChip}
          />
          <Chip
            size="small"
            variant="outlined"
            label={t("widget.draftSummaryParams", { count: paramCount })}
            sx={styles.draftSummaryChip}
          />
        </Box>
      </Stack>
    </Box>
  );
}
